import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { validateYupWithFeedback } from "services/forms/validation/feedback";
import { EntityItem } from "services/api/notifications/types";
import { ENTITY_COLUMNS } from "./consts";
import { Pagination } from "services/search/types";
import { DataWithPagination } from "services/api/types";
import {
  ENTITY_TYPE_LABELS,
  initialEntityItemPagination,
} from "services/api/notifications/consts";
import { getEntityItemsPagedAPI } from "services/api/notifications/api";
import { TableSearch } from "ui/components/Table/TableSearch";
import { TextField } from "ui/components/TextField/TextField";
import { NOTIFICATION_STEP2_ENTITY_SCHEMA } from "../validation";
import { StepProps } from "../types";
import { NoResults } from "ui/components/NoResults";
import Badge from "ui/components/Badge/Badge";

const Step2EntitySelection: React.FC<StepProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
  registerValidator,
}) => {
  const [entityData, setEntityData] = useState<DataWithPagination<EntityItem>>({
    data: [],
    pagination: initialEntityItemPagination,
  });
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(
    initialEntityItemPagination
  );

  useEffect(() => {
    if (registerValidator) {
      registerValidator(() =>
        validateYupWithFeedback(formValues, NOTIFICATION_STEP2_ENTITY_SCHEMA, setErrors)
      );
    }
  }, [formValues]);

  const fetchData = useCallback(
    async (entityType: number, pag: Pagination, searchTerm: string) => {
      setIsLoading(true);
      try {
        const result = await getEntityItemsPagedAPI(
          entityType,
          pag,
          searchTerm || undefined
        );
        setEntityData(result);
        setPagination(result.pagination);
      } catch {
        // error handled by createApiCall
      }
      setIsLoading(false);
    },
    []
  );

  // Fetch on entityType change — reset pagination and search
  useEffect(() => {
    if (formValues.entityType) {
      const resetPag = { ...initialEntityItemPagination };
      setPagination(resetPag);
      setSearch("");
      fetchData(formValues.entityType, resetPag, "");
    }
  }, [formValues.entityType]);

  // Search change (debounced by TextField) — reset to page 1, fetch
  const handleSearchChange = useCallback(
    (e: any) => {
      const value = e.target.value;
      setSearch(value);
      if (formValues.entityType) {
        const resetPag = { ...pagination, pageNumber: 1 };
        setPagination(resetPag);
        fetchData(formValues.entityType, resetPag, value);
      }
    },
    [formValues.entityType, pagination, fetchData]
  );

  // Pagination change from TableSearch
  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      setPagination(newPagination);
      if (formValues.entityType) {
        fetchData(formValues.entityType, newPagination, search);
      }
    },
    [formValues.entityType, search, fetchData]
  );

  const selectedSet = useMemo(
    () => new Set(formValues.includedEntityIds),
    [formValues.includedEntityIds]
  );

  // Only current page's selected IDs — so header checkbox works correctly
  const currentPageSelectedIds = useMemo(
    () => entityData.data.filter((e) => selectedSet.has(e.id)).map((e) => e.id),
    [entityData.data, selectedSet]
  );

  const handleSelectedChange = useCallback(
    (selected: number[] | number) => {
      if (Array.isArray(selected)) {
        if (selected.length === 0) {
          // Deselect all on current page
          const pageIds = new Set(entityData.data.map((e) => e.id));
          setFormValues((prev) => ({
            ...prev,
            includedEntityIds: prev.includedEntityIds.filter(
              (id) => !pageIds.has(id)
            ),
          }));
        } else {
          // Select all on current page
          const ids = selected as any as string[];
          setFormValues((prev) => {
            const next = new Set(prev.includedEntityIds);
            ids.forEach((id) => next.add(id));
            return { ...prev, includedEntityIds: Array.from(next) };
          });
        }
      } else {
        // Toggle single
        const id = selected as any as string;
        setFormValues((prev) => {
          const next = new Set(prev.includedEntityIds);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return { ...prev, includedEntityIds: Array.from(next) };
        });
      }
      setErrors(({ includedEntityIds, ...rest }) => rest);
    },
    [entityData.data, setFormValues, setErrors]
  );

  const entityLabel =
    formValues.entityType != null
      ? ENTITY_TYPE_LABELS[formValues.entityType]?.toLowerCase()
      : "entitete";

  const columns = ENTITY_COLUMNS;

  return (
    <div className="flex justify-center h-full px-4 mt-[100px]">
      <div className="w-full max-w-4xl">
        <div className="flex flex-col gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Odabir entiteta
            </h1>
            <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
              Odaberite {entityLabel} za koje želite slati notifikaciju
            </p>
          </div>
          {!isLoading && entityData.data.length === 0 ? (
            <NoResults message={`Nema dostupnih ${entityLabel} za odabir`} />
          ) : (
            <div className="border border-dashed bg-white border-blue-300 rounded-sm px-3 pt-4 pb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="w-64">
                  <TextField
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Pretraži..."
                    startIcon="/media/icons/search.svg"
                  />
                </div>
                <Badge
                  text={`${formValues.includedEntityIds.length} odabrano`}
                  variant="info"
                  height="small"
                />
              </div>

              <TableSearch
                data={entityData.data}
                columns={columns}
                selectableRows={true}
                showSelectAll={true}
                selectedRows={currentPageSelectedIds as any}
                onSelectedChange={handleSelectedChange}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                isLoading={isLoading}
              />

              {errors.includedEntityIds && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.includedEntityIds}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(Step2EntitySelection);
